import React, {PropsWithChildren, useEffect, useRef, useState} from "react";
import ReactDOM from "react-dom";
import optionsIcon from "assets/Icon_Btn_05.png";
import classNames from "classnames";

type OptionsButtonProps = {
  config?: any,
  visibleRowOptionListener?(id:number|null): void;
  visibleOptionsMenu: number | null;
  setVisibleOptionsMenu(id: number | null): void;
  userName?: string;
  setUserName?(name: string | null): void;
  setChartType?: void;
  dashboardOptionsButton?: boolean;
};

type Props = OptionsButtonProps;


const OptionsButton: React.FC<PropsWithChildren<Props>> = ({
                                                             config,
                                                             visibleRowOptionListener,
                                                             visibleOptionsMenu,
                                                             setVisibleOptionsMenu,
                                                             userName,
                                                             setUserName,
                                                             dashboardOptionsButton,
                                                             children
                                                           }) => {
  const [optionsMenuOpen, setOptionsMenuOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});

  // Standard tables wrap their rows in `.TableWrapper { overflow: hidden }`, which clips this
  // absolutely-positioned dropdown for rows near the bottom of the table — the last row shows
  // no options at all. For those, portal the menu to <body> with fixed positioning so it
  // escapes the clip. The dashboard variant lives outside that wrapper, so leave it as-is.
  const usePortal = !dashboardOptionsButton;

  const classnames = classNames("TableOptions", { DashboardOptionsButton: dashboardOptionsButton })

  // Position the portaled menu just under the trigger (or above it if there isn't room below).
  const positionMenu = () => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const MENU_WIDTH = 150;
    const MENU_EST_HEIGHT = 100;
    const openUp =
      window.innerHeight - rect.bottom < MENU_EST_HEIGHT && rect.top > MENU_EST_HEIGHT;
    setMenuStyle({
      position: "fixed",
      top: openUp ? "auto" : rect.bottom + 4,
      bottom: openUp ? window.innerHeight - rect.top + 4 : "auto",
      left: Math.max(8, rect.right - MENU_WIDTH),
      right: "auto",
      zIndex: 1000,
    });
  };

  const handleClickOutside = (event: any, ref: any) => {
    if (ref.current) {
      if (!ref.current.contains(event.target)) {
        //setVisibleOptionsMenu(null);
        setTimeout(() => {
          setOptionsMenuOpen(false);
        }, 300);
      }
    }
  };

  useEffect(() => {
    if (!optionsMenuOpen) {
      return;
    }
    const clickOutsideListenerFunction = (e: any) => handleClickOutside(e, wrapperRef);
    document.addEventListener("click", clickOutsideListenerFunction);
    // A fixed/portaled menu won't follow page scroll, so close it on scroll rather than let it drift.
    const closeOnScroll = () => {
      setVisibleOptionsMenu(null);
      setOptionsMenuOpen(false);
    };
    if (usePortal) {
      window.addEventListener("scroll", closeOnScroll, true);
    }

    return () => {
      document.removeEventListener("click", clickOutsideListenerFunction);
      if (usePortal) {
        window.removeEventListener("scroll", closeOnScroll, true);
      }
    };
  }, [optionsMenuOpen]);

  const handleUserMenuClick = () => {
    if (optionsMenuOpen) {
      setVisibleOptionsMenu(null);
      setTimeout(() => {
        setOptionsMenuOpen(false);
        if (visibleRowOptionListener) {
          visibleRowOptionListener(null)
        }
      }, 300);
    } else {
      if (usePortal) {
        positionMenu();
      }
      setOptionsMenuOpen(true);
      setTimeout(() => {
        setVisibleOptionsMenu(config.id);
        if (setUserName) setUserName(config.name);
        if (visibleRowOptionListener) {
          visibleRowOptionListener(config.id)
        }
      }, 10);
    }
  };

  const menuClassName = optionsMenuOpen
    ? (visibleOptionsMenu === config.id ? "Menu Visible" : "Menu")
    : "Menu Closed";

  const menu = (
    <div className={menuClassName} style={usePortal ? menuStyle : undefined}>
      { children }
    </div>
  );

  return (
    <div ref={wrapperRef}>
      <button ref={buttonRef} className={classnames} onClick={ handleUserMenuClick }><img src={ optionsIcon } alt="Options" /></button>
      { usePortal
        ? (optionsMenuOpen ? ReactDOM.createPortal(menu, document.body) : null)
        : menu }
    </div>
  )
};

export default OptionsButton;
